import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { Twitter, Youtube, Send, Dribbble, Video, Heart } from "lucide-react";

const SOCIALS = [
  { name: "Twitter", icon: Twitter, href: "#" },
  { name: "YouTube", icon: Youtube, href: "#" },
  { name: "Vimeo", icon: Video, href: "#" },
  { name: "Telegram", icon: Send, href: "#" },
  { name: "Dribbble", icon: Dribbble, href: "#" },
];

export default function About() {
  const { t, lang } = useLang();
  const [site, setSite] = useState(null);
  useEffect(() => {
    api.get("/site/about").then(({ data }) => setSite(data?.data || null)).catch(() => setSite(null));
  }, []);
  const pick = (obj, fallback = "") =>
    (obj && typeof obj === "object" && (obj[lang] || obj.en || obj.ru || obj.es)) || fallback;
  const title = pick(site?.title, t.about.title);
  const role = pick(site?.role, t.about.role);
  const bio = pick(site?.bio, t.about.bio);
  const quote = pick(site?.quote, "Drawing every frame by hand is slow on purpose. It is the only way I know how to make things feel alive.");
  const stats = (site?.stats && site.stats.length) ? site.stats.map((s) => ({ value: s.value, label: pick(s.label) })) : t.about.stats;
  const imageA = site?.image_a || "https://images.unsplash.com/photo-1572291244855-44aa55da2137?auto=format&fit=crop&w=900&q=80";
  const imageB = site?.image_b || "https://images.pexels.com/photos/34411122/pexels-photo-34411122.jpeg?auto=compress&cs=tinysrgb&w=1200";
  return (
    <section id="about" className="relative bg-[#faf6f0] py-24 lg:py-32 border-t border-[#0a0a0a] overflow-hidden">
      {/* Soft pastels */}
      <div className="absolute inset-0 pointer-events-none opacity-60">
        <div className="absolute top-10 right-10 w-[480px] h-[480px] rounded-full bg-[#ffd9ec] blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#d8f0ff] blur-3xl" />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Portrait / workspace stack */}
        <div className="lg:col-span-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-6 flex items-center gap-2">
            <Heart size={11} className="text-[#ff006e]" fill="currentColor" />
            · {t.about.label}
          </div>
          <div className="relative">
            {/* Back image */}
            <motion.div
              initial={{ opacity: 0, y: 20, rotate: -3 }}
              whileInView={{ opacity: 1, y: 0, rotate: -3 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="absolute -top-6 -left-4 w-[55%] aspect-[3/4] bg-white border border-[#0a0a0a] overflow-hidden hidden sm:block"
              style={{ boxShadow: "8px 8px 0 0 #00d4ff" }}
            >
              <img
                src={imageA}
                alt="Studio sketch"
                className="w-full h-full object-cover"
              />
            </motion.div>
            {/* Front image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative ml-auto w-[88%] aspect-[4/5] bg-[#f5f5f5] border border-[#0a0a0a] overflow-hidden"
              style={{ boxShadow: "10px 10px 0 0 #ff006e" }}
            >
              <img
                src={imageB}
                alt="Studio workspace"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between bg-white px-3 py-2 border border-[#0a0a0a]">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0a0a0a]">studio_001.jpg</span>
                <span className="inline-block w-1.5 h-1.5 bg-[#00d4ff] pulse-dot" />
              </div>
            </motion.div>
          </div>
        </div>

        <div className="lg:col-span-6 flex flex-col justify-center">
          <h2 className="font-mono font-bold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-[#0a0a0a]">
            {title}
          </h2>
          <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-[#ff006e]">
            {role}
          </div>

          {/* Decorative pull quote */}
          <div className="mt-8 border-l-2 border-[#ff006e] pl-5 italic font-sans text-lg lg:text-xl text-[#0a0a0a]/85 leading-snug">
            <span className="text-[#ff006e] font-mono">&ldquo;</span>
            {quote}
            <span className="text-[#ff006e] font-mono">&rdquo;</span>
          </div>

          <p className="mt-8 font-sans text-base lg:text-lg text-[#4a4a4a] leading-relaxed max-w-2xl">
            {bio}
          </p>

          <div className="mt-10 grid grid-cols-3 gap-px bg-[#0a0a0a] border border-[#0a0a0a] max-w-md">
            {stats.map((s, i) => (
              <div key={i} className="bg-white p-5">
                <div className="font-mono font-bold text-3xl lg:text-4xl tracking-tighter text-[#0a0a0a]">
                  {s.value}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a]">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            {SOCIALS.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.name}
                  href={s.href}
                  className="group inline-flex items-center gap-2 px-4 py-2 border border-[#0a0a0a] font-mono text-[11px] uppercase tracking-[0.2em] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors bg-white/60 backdrop-blur-sm"
                >
                  <Icon size={14} />
                  {s.name}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
